import type { UseFormReturn } from 'react-hook-form';
import { RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ProfileField } from './types';

export const profileObjectValues = (value: string, fields: NonNullable<ProfileField['objectFields']>): Record<string, string | null> | null => {
  if (!value) return {};
  let parsed: unknown;
  try { parsed = JSON.parse(value); } catch { return null; }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  if (Object.entries(parsed).some(([name, value]) => !fields.some(field => field.name === name) || (value !== null && typeof value !== 'string'))) return null;
  return parsed as Record<string, string | null>;
};

export const ProfileObjectFields = ({ form, entity, field, disabled, requiredParts = [] }: {
  form: UseFormReturn<Record<string, string>>; entity: string; field: ProfileField; disabled: boolean;
  requiredParts?: readonly string[];
}) => {
  const parts = field.objectFields ?? [];
  const raw = form.watch(field.name) ?? '';
  const values = profileObjectValues(raw, parts);
  const change = (value: string) => form.setValue(field.name, value, { shouldDirty: true, shouldValidate: true });
  const id = `profile-${entity}-${field.name}`;
  return <fieldset className="space-y-3 rounded border p-3" disabled={disabled}>
    <legend className="px-1 font-medium">{field.label}</legend>
    <input id={`${id}-stored`} type="hidden" {...form.register(field.name, { required: field.required, validate: value => {
      if (!requiredParts.length) return true;
      const contact = profileObjectValues(value, parts);
      return Boolean(contact && requiredParts.every(name => contact[name]?.trim()));
    } })} />
    {values === null && <><p role="alert">The retained {field.label.toLowerCase()} needs review. Its stored value will be preserved until replaced.</p><pre className="whitespace-pre-wrap break-words text-sm">{raw}</pre><Button id={`${id}-replace`} variant="outline" type="button" onClick={() => change('{}')}><RotateCcw aria-hidden="true" className="mr-2 h-4 w-4" />Replace {field.label.toLowerCase()}</Button></>}
    {values !== null && <>
      <div className="grid gap-3 md:grid-cols-2">{parts.map(part => <div key={part.name}><Label htmlFor={`${id}-${part.name}`}>{field.label}: {part.label}{requiredParts.includes(part.name) ? ' (required)' : ''}</Label><Input id={`${id}-${part.name}`} className="h-8" type={part.format === 'email' ? 'email' : 'text'} aria-required={requiredParts.includes(part.name)} value={values[part.name] ?? ''} onChange={event => change(JSON.stringify({ ...values, [part.name]: event.target.value }))} /></div>)}</div>
      {!field.required && <Button id={`${id}-clear`} variant="outline" type="button" onClick={() => change('')}><X aria-hidden="true" className="mr-2 h-4 w-4" />Clear {field.label.toLowerCase()}</Button>}
    </>}
  </fieldset>;
};
